import type { Country, Currency } from "@meditect/medibase-api-client";

/** Tout ce dont le formatage a besoin : le reste de `Currency` (id, dates) ne sert pas ici. */
export type DisplayCurrency = Pick<Currency, "code" | "decimals" | "symbol">;

type FormatCurrencyOptions = { sanitizeSpace?: boolean; currency?: DisplayCurrency };

export const formatCurrency = (
  amount: number,
  country?: Country,
  hideSymbol = false,
  options?: FormatCurrencyOptions,
): string => {
  let formatedAmount = formatAmount(amount, {
    country,
    currency: options?.currency,
    isCurrency: true,
    hideSymbol,
  });
  if (options?.sanitizeSpace) {
    formatedAmount = sanitizeFormattedAmount(formatedAmount);
  }
  return formatedAmount;
};

export type FormatAmountOptions = {
  country?: Country;
  isCurrency?: boolean;
  hideSymbol?: boolean;
  currency?: DisplayCurrency;
};
export const formatAmount = (amount: number, options?: FormatAmountOptions): string => {
  const { country, currency: currencyOverride, isCurrency = false, hideSymbol = false } = options || {};
  const currency = currencyOverride || country?.currency;
  const validCurrency = !!(isCurrency && currency);
  let numberFormatOptions: Intl.NumberFormatOptions = {};
  if (validCurrency) {
    if (hideSymbol) {
      numberFormatOptions = {
        ...numberFormatOptions,
        maximumFractionDigits: currency.decimals,
        minimumFractionDigits: currency.decimals,
      };
    } else {
      numberFormatOptions = {
        ...numberFormatOptions,
        currency: currency.code,
        style: "currency",
      };
    }
  }
  let formatted = Intl.NumberFormat(country?.locale ?? "fr", numberFormatOptions).format(
    validCurrency ? amount / 10 ** currency.decimals : amount,
  );

  // FIX: Some currencies are missing from Chromium Android's Intl implementation.
  // They will be formatted with the code instead of the symbol, so we need to
  // replace the code with the symbol we know from the database.
  // Ex. Instead of "5 000 F CFA", Intl returns "5 000 XOF"
  if (validCurrency) {
    formatted = formatted.replace(currency.code, currency.symbol);
  }

  return formatted;
};

export const sanitizeFormattedAmount = (amount: string): string => {
  return amount
    .replace(/\u00A0/g, " ") // non-breaking space
    .replace(/\u202F/g, " "); // narrow non-breaking space
};

/** Parse un montant saisi (espaces, virgule ou point décimal). */
const DECIMAL_AMOUNT_PATTERN = /^-?\d+(\.\d+)?$/;

/** Convertit une chaîne saisie en unités mineures sans passer par un flottant majeur. */
export const parseAmountToMinor = (value: string, decimals: number): number | null => {
  const normalized = sanitizeFormattedAmount(value).replace(/\s/g, "").replace(",", ".");
  if (normalized === "" || normalized === "-" || normalized === ".") {
    return null;
  }
  if (!DECIMAL_AMOUNT_PATTERN.test(normalized)) {
    return null;
  }

  const negative = normalized.startsWith("-");
  const unsigned = negative ? normalized.slice(1) : normalized;
  const [integerPart, fractionalPart = ""] = unsigned.split(".");

  if (fractionalPart.length > decimals) {
    const major = Number.parseFloat(unsigned);
    if (Number.isNaN(major)) {
      return null;
    }

    return (negative ? -1 : 1) * Math.round(major * 10 ** decimals);
  }

  const paddedFraction = fractionalPart.padEnd(decimals, "0").slice(0, decimals);
  const integerMinor = Number.parseInt(integerPart, 10) * 10 ** decimals;
  const fractionalMinor = decimals === 0 ? 0 : Number.parseInt(paddedFraction, 10);

  return (negative ? -1 : 1) * (integerMinor + fractionalMinor);
};
